<template>
  <DashboardLayout>
    <div class="d-flex align-items-center gap-2 mb-4 page-identifier">
      <router-link :to="{ name: 'ViewWarrants' }" class="mb-0 main-identifier fw-semibold text-decoration-none"
        title="Go back to Warrants">
        Warrants
      </router-link> <span class="material-symbols-outlined">keyboard_arrow_right</span>
      <p class="mb-0 sub-identifier">Payment Voucher Generation Form</p>
    </div>

    <div class="d-flex justify-content-center mb-5">
      <div class="form-outer">
        <form @submit.prevent="handleSubmit">
          <div class="crud-form position-relative">
            <div class="form-inner mb-2">
              <div class="form-header d-flex justify-content-between align-items-center mb-4">
                <h1 class="mb-0">{{ formTitle }}</h1>
                <button v-if="isMemoMode && memoData" type="button" class="btn btn-sm memo-preview-btn"
                  @click="openMemoPreview">
                  View Memo Details
                </button>
              </div>

              <div class="form-body">
                <!-- Top Section with Border -->
                <div class="top-section-border mb-4">
                  <div class="row mb-3">
                    <!-- Beneficiary -->
                    <div class="col-md-6 form-group">
                      <div class="beneficiary-header mb-2">
                        <label class="form-label mb-0">Beneficiary</label>
                        <div class="checkbox-group">
                          <label class="checkbox-label">
                            <input type="checkbox" v-model="form.isMDA" @change="onBeneficiaryTypeChange('mda')"
                              id="mdaCheck">
                            <span>MDA</span>
                          </label>
                          <label class="checkbox-label">
                            <input type="checkbox" v-model="form.isConsultant"
                              @change="onBeneficiaryTypeChange('consultant')" id="consultantCheck">
                            <span>Consultant/Contractor</span>
                          </label>
                        </div>
                      </div>

                      <!-- MDA Dropdown -->
                      <SearchableSelect v-if="form.isMDA" :key="'mda-select'" v-model="form.beneficiary" :options="beneficiaries"
                        :loading="loadingMDAOptions" loading-text="Loading MDAs..." placeholder="- Select MDA -"
                        required />

                      <!-- Consultant/Contractor Dropdown -->
                      <SearchableSelect v-if="form.isConsultant" :key="'consultant-select'" v-model="form.beneficiary" :options="consultantOptions"
                        :loading="loadingConsultantOptions" loading-text="Loading Consultants..."
                        placeholder="- Select Consultant/Contractor -" required />

                    </div>

                    <!-- Payment Type -->
                    <div class="col-md-6 form-group">
                      <label for="paymentType" class="form-label mb-2">Payment Type</label>
                      <select class="form-select payment-type-select" v-model="form.paymentType" required>
                        <option value="" disabled>- Select Payment Type -</option>
                        <option v-for="type in paymentTypes" :key="type.name" :value="type.name">{{ type.name }}
                        </option>
                      </select>
                    </div>
                  </div>

                  <div class="row mb-3" v-if="form.paymentType === 'Cheque'">
                    <!-- Cheque Number -->
                    <div class="col-md-6 form-group">
                      <label for="chequeNumber" class="form-label mb-2">Cheque Number</label>
                      <input id="chequeNumber" type="text" class="form-control" v-model="form.chequeNumber"
                        required />
                    </div>
                  </div>

                  <div v-if="form.isConsultant && !isMemoMode" :key="'originating-mda-select'" class="row mb-3">
                    <div class="col-md-6 form-group">
                      <label class="form-label mb-2">Originating MDA</label>
                      <SearchableSelect v-model="form.originatingMdaId" :options="beneficiaries"
                        :loading="loadingMDAOptions" loading-text="Loading Originating MDAs..."
                        placeholder="- Select Originating MDA -" required />
                    </div>
                  </div>

                  <div class="row mb-3">
                    <!-- Bank -->
                    <div class="col-md-6 form-group">
                      <label for="bank" class="form-label mb-2">Bank</label>
                      <select class="form-select" v-model="form.bank" :disabled="loadingBanks" required>
                        <option value="" disabled>{{ loadingBanks ? 'Loading Banks...' : '- Select Bank -' }}</option>
                        <option v-for="bank in banks" :key="bank.id" :value="bank.id">{{ bank.name }}</option>
                      </select>
                    </div>

                    <!-- Gross Amount -->
                    <div class="col-md-6 form-group">
                      <label for="grossAmount" class="form-label mb-2">Gross Amount</label>
                      <input id="grossAmount" type="text" class="form-control" :value="formatCurrency(form.grossAmount)"
                        inputmode="numeric" @keypress="onlyDigitsAndComma" @input="onGrossAmountInput"
                        placeholder="₦0.00" required />
                    </div>
                  </div>

                  <div class="row mb-3">
                    <!-- Nature Of Expenditure -->
                    <div class="col-md-4 form-group">
                      <label for="natureOfExpenditure" class="form-label mb-2">Nature Of Expenditure</label>
                      <select class="form-select " v-model="form.natureOfExpenditure" required>
                        <option value="" disabled>- Select Nature Of Expenditure -</option>
                        <option v-for="type in natureOfExpenditureTypes" :key="type.name" :value="type.name">{{
                          type.name }}
                        </option>
                      </select>
                    </div>

                    <!-- Net Amount -->
                    <div class="col-md-4 form-group">
                      <label for="netAmount" class="form-label mb-2">Net Amount</label>
                      <input type="text" class="form-control" :value="formatCurrency(netAmount)" readonly />
                    </div>


                    <!-- Initiated Date -->
                    <div class="col-md-4 form-group">
                      <label for="initiatedDate" class="form-label mb-2">Initiated Date</label>
                      <input type="date" class="form-control" v-model="form.initiatedDate" readonly />
                    </div>
                  </div>

                  <div class="row mb-3">


                  </div>

                  <div class="row mb-3">
                    <!-- Particulars -->
                    <div class="col-md-12 form-group">
                      <label for="particulars" class="form-label mb-2">Particulars</label>
                      <textarea rows="3" class="form-control" v-model="form.particulars" required></textarea>
                    </div>
                  </div>
                </div>


                <!-- Deductions Table -->
                <div class="mb-4">
                  <div class="table-responsive">
                    <table class="table table-bordered text-center align-middle">
                      <thead class="table-header">
                        <tr>
                          <th>Description</th>
                          <th>Percentage</th>
                          <th>Amount</th>
                          <th style="width: 110px;">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-if="deductions.length === 0">
                          <td colspan="4" class="text-muted">No deductions added</td>
                        </tr>
                        <tr v-for="(deduction, index) in deductions" :key="index"
                          :class="index % 2 === 0 ? 'row-even' : 'row-odd'">
                          <td data-label="Description">{{ deduction.description || deduction.name }}</td>
                          <td data-label="Percentage">
                            {{ deduction.inputMode === 'percentage' ? `${Number(deduction.percentage || 0).toFixed(2)}%`
                            : '-' }}
                          </td>
                          <td data-label="Amount">{{ formatCurrency(deduction.amount) }}</td>
                          <td class="deduction-actions">
                            <button type="button" class="btn btn-outline-secondary btn-sm"
                              @click="openDeductionModalForEdit(index)">
                              <span class="iconify" data-icon="mdi:pencil-outline"></span>
                            </button>
                            <button type="button" class="btn btn-outline-danger btn-sm" @click="removeDeduction(index)">
                              <span class="iconify" data-icon="mdi:trash-can-outline"></span>
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <!-- Add Deduction Section -->
                  <div class="add-section">
                    <p class="section-title mb-3 text-center">Select Deduction to Add:</p>
                    <div class="add-controls-wrapper">
                      <div class="add-controls">
                        <div class="filter-group">
                          <label class="control-label">Filter Options</label>
                          <input type="text" v-model="deductionSearchTerm" placeholder="Search title/code"
                            class="form-control form-control-sm" />
                        </div>

                        <div class="select-group">
                          <label class="control-label">Choose Deductions</label>
                          <select class="form-select form-select-sm" v-model="selectedDeductionId"
                            :disabled="loadingDeductionOptions">
                            <option value="" disabled>
                              {{ loadingDeductionOptions ? 'Loading deductions...' : '-- pick deduction --' }}
                            </option>
                            <option v-for="opt in filteredDeductionOptions" :key="opt.id" :value="opt.id">
                              {{ opt.name }}
                            </option>
                          </select>
                        </div>

                        <div class="button-group">
                          <label class="control-label">&nbsp;</label>
                          <button type="button" class="btn btn-outline-primary btn-sm"
                            :disabled="!selectedDeductionId || loadingDeductionOptions"
                            @click="openDeductionModalForCreate">
                            <span class="iconify" data-icon="mdi:plus"></span>
                            Configure & Add
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Submit Button -->
                <div class="d-flex justify-content-center mt-4">
                  <button type="submit" class="btn btn-submit" :disabled="loading">
                    <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"
                      aria-hidden="true"></span>
                    {{ loading ? 'Submitting...' : submitLabel }}
                  </button>
                </div>

              </div>
            </div>
          </div>
        </form>
      </div>
    </div>

    <div v-if="showDeductionModal" class="deduction-modal-overlay" @click="closeDeductionModal">
      <div class="deduction-modal" @click.stop>
        <div class="deduction-modal-header">
          <h5 class="mb-0">{{ deductionModalMode === 'edit' ? 'Edit Deduction' : 'Add Deduction' }}</h5>
          <button type="button" class="btn-close" aria-label="Close" @click="closeDeductionModal"></button>
        </div>

        <div class="deduction-modal-body">
          <div class="deduction-selected mb-3">
            <small>Deduction Type</small>
            <p class="mb-0">{{ deductionDraft.name || 'N/A' }}</p>
          </div>

          <div class="mb-3">
            <!-- <label class="form-label mb-2">How do you want to enter this deduction?</label> -->
            <div class="deduction-input-mode">
              <label class="checkbox-label" :class="{ 'mode-disabled': !isPercentageModeDeduction }">
                <input type="radio" value="percentage" v-model="deductionDraft.inputMode"
                  :disabled="!isPercentageModeDeduction" @change="enforceDeductionInputMode">
                <span>Percentage (%)</span>
              </label>
              <label class="checkbox-label" :class="{ 'mode-disabled': isPercentageModeDeduction }">
                <input type="radio" value="amount" v-model="deductionDraft.inputMode"
                  :disabled="isPercentageModeDeduction" @change="enforceDeductionInputMode">
                <span>Fixed Amount (₦)</span>
              </label>
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label mb-2">{{ deductionDraft.inputMode === 'percentage' ? 'Percentage' : 'Amount'
            }}</label>
            <input v-if="deductionDraft.inputMode === 'percentage'" type="number" class="form-control"
              v-model.number="deductionDraft.value" min="0" max="100" step="0.01" inputmode="decimal"
              @keydown="preventNonNumericInput" placeholder="Enter percentage" />
            <input v-else type="number" class="form-control" v-model.number="deductionDraft.value" min="0" step="0.01"
              :max="form.grossAmount" inputmode="decimal" @keydown="preventNonNumericInput"
              placeholder="Enter amount" />
          </div>

          <div class="deduction-preview-grid">
            <div class="deduction-preview-item">
              <small>Gross Amount</small>
              <p class="mb-0">{{ formatCurrency(form.grossAmount) }}</p>
            </div>
            <div class="deduction-preview-item">
              <small>Computed Percentage</small>
              <p class="mb-0">{{ Number(modalCalculatedPercentage).toFixed(2) }}%</p>
            </div>
            <div class="deduction-preview-item">
              <small>Computed Amount</small>
              <p class="mb-0">{{ formatCurrency(modalCalculatedAmount) }}</p>
            </div>
          </div>

          <p class="deduction-hint mt-3 mb-0">
            Tip: Percentage is always calculated from the current gross amount.
          </p>
        </div>

        <div class="deduction-modal-footer">
          <button type="button" class="btn btn-outline-secondary btn-sm" @click="closeDeductionModal">Cancel</button>
          <button type="button" class="btn btn-outline-primary btn-sm"
            :disabled="Number(deductionDraft.value || 0) <= 0" @click="saveDeductionFromModal">
            {{ deductionModalMode === 'edit' ? 'Save Changes' : 'Add Deduction' }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="showMemoPreview" class="warrant-preview-overlay" @click="closeMemoPreview">
      <div class="warrant-preview-modal" @click.stop>
        <div class="warrant-preview-header">
          <h5 class="mb-0">Memo Details</h5>
          <button type="button" class="btn-close" aria-label="Close" @click="closeMemoPreview"></button>
        </div>

        <div class="warrant-preview-body">
          <div class="preview-grid mb-3">
            <div class="preview-item">
              <small>File Title</small>
              <p class="mb-0">{{ memoData?.fileTitle || 'N/A' }}</p>
            </div>
            <div class="preview-item">
              <small>File Number</small>
              <p class="mb-0">{{ memoData?.fileNumber || 'N/A' }}</p>
            </div>
            <div class="preview-item">
              <small>Originating MDA</small>
              <p class="mb-0">{{ memoData?.originatingMda || 'N/A' }}</p>
            </div>
            <div class="preview-item">
              <small>Amount Approved</small>
              <p class="mb-0">{{ formatCurrency(Number(memoData?.amountApproved || 0)) }}</p>
            </div>
            <div class="preview-item">
              <small>Created Date</small>
              <p class="mb-0">{{ formatMemoDate(memoData?.createdDate) }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

  </DashboardLayout>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import axios from 'axios'
import DashboardLayout from '../layout/DashboardLayout.vue'
import SearchableSelect from '../common/SearchableSelect.vue'
import Cookies from 'js-cookie'
import Swal from 'sweetalert2'
import { useRoute, useRouter } from 'vue-router'

const route = useRoute()
const router = useRouter()
const pvIdFromQuery = computed(() => route.query.pvId ? String(route.query.pvId) : null)
const memoIdFromParams = computed(() => route.params.memoId ? String(route.params.memoId) : null)
const memoIdFromQuery = computed(() => route.query.memoId ? String(route.query.memoId) : null)
const memoIdFromRoute = computed(() => memoIdFromParams.value || memoIdFromQuery.value || null)
const isEditMode = computed(() => Boolean(pvIdFromQuery.value))
const isMemoMode = computed(() => Boolean(memoIdFromRoute.value))

const root_api_url = process.env.VUE_APP_API_ROOT_URL
const loading = ref(false)
const loadingBanks = ref(false)
const loadingMDAOptions = ref(false)
const loadingConsultantOptions = ref(false)
const loadingDeductionOptions = ref(false)
const loadingMemo = ref(false)
const showMemoPreview = ref(false)
const iconHtmlError = `<span class="iconify" data-icon="material-symbols:cancel-outline-rounded" style="font-size: 100px; color: red;"></span>`
const existingPvPayload = ref(null)
const memoData = ref(null)

const form = reactive({
  isMDA: true,
  isConsultant: false,
  beneficiary: '',
  payee: '',
  originatingMdaId: '',
  paymentType: '',
  chequeNumber: '',
  bank: '',
  grossAmount: 0,
  initiatedDate: '',
  particulars: '',
  natureOfExpenditure: '',
  memoId: '',
  mdaName: ''
})

const beneficiaries = ref([])
const consultantOptions = ref([])
const paymentTypes = ref([
  { name: 'Transfer' },
  { name: 'Cheque' }
])
const natureOfExpenditureTypes = ref([
  { name: 'Capital' },
  { name: 'Recurrent' },
  { name: 'Hybrid' }
])
const banks = ref([])
const deductions = ref([])
const deductionOptions = ref([])
const selectedDeductionId = ref('')
const deductionSearchTerm = ref('')
const showDeductionModal = ref(false)
const deductionModalMode = ref('create')
const editingDeductionIndex = ref(null)
const deductionDraft = reactive({
  id: '',
  name: '',
  description: '',
  dedTypeAmt: 0,
  dedTypeMode: 0,
  dedSetByUserInd: 0,
  dedPercentByUser: 0,
  inputMode: 'percentage',
  value: 0
})

// const economicLineItems = ref([])
// const ecoOptions = ref([])
// const selectedEcoId = ref('')
// const ecoSearchTerm = ref('')

const filteredDeductionOptions = computed(() =>
  deductionOptions.value.filter(item =>
    (item.name || '').toLowerCase().includes(deductionSearchTerm.value.toLowerCase())
  )
)

const formTitle = computed(() => {
  if (isEditMode.value) return 'Edit Returned Payment Voucher'
  if (isMemoMode.value) return 'Generate Payment Voucher from Memo'
  return 'Payment Voucher Generation Form (No Warrant)'
})
const submitLabel = computed(() => {
  if (isEditMode.value) return 'Update Voucher'
  if (isMemoMode.value) return 'Generate PV'
  return 'Submit'
})

// const filteredEcoOptions = computed(() =>
//   ecoOptions.value.filter(item =>
//     [item.ecoCode, item.ecoLineItem, item.description]
//       .join(' ')
//       .toLowerCase()
//       .includes(ecoSearchTerm.value.toLowerCase())
//   )
// )

const totalDeductions = computed(() =>
  deductions.value.reduce((sum, ded) => sum + (ded.amount || 0), 0)
)

const netAmount = computed(() =>
  Math.max(0, form.grossAmount - totalDeductions.value)
)

const modalCalculatedAmount = computed(() => {
  const value = Number(deductionDraft.value || 0)
  if (value <= 0) return 0

  if (deductionDraft.inputMode === 'percentage') {
    return Number(((form.grossAmount * value) / 100).toFixed(2))
  }

  return Number(value.toFixed(2))
})

const modalCalculatedPercentage = computed(() => {
  const amount = Number(modalCalculatedAmount.value || 0)
  if (amount <= 0 || form.grossAmount <= 0) return 0
  return Number(((amount / form.grossAmount) * 100).toFixed(2))
})

const isPercentageModeDeduction = computed(() => Number(deductionDraft.dedTypeMode || 0) === 1)

const enforceDeductionInputMode = () => {
  deductionDraft.inputMode = isPercentageModeDeduction.value ? 'percentage' : 'amount'
}

const onBeneficiaryTypeChange = (type) => {
  if (type === 'mda') {
    if (form.isMDA) {
      form.isConsultant = false
      form.beneficiary = ''
      form.originatingMdaId = ''
      form.payee = ''
    }
  } else if (type === 'consultant') {
    if (form.isConsultant) {
      form.isMDA = false
      form.beneficiary = ''
      form.originatingMdaId = ''
      form.payee = ''
    }
  }
}

const formatCurrency = (val) => {
  if (val === '' || val === null || val === undefined) return ''
  const num = Number(val)
  if (isNaN(num)) return ''
  return '₦' + num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const onlyDigitsAndComma = (e) => {
  const k = e.key
  const allowed = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab', 'Home', 'End']
  if ((k >= '0' && k <= '9') || allowed.includes(k)) return
  e.preventDefault()
}

const preventNonNumericInput = (e) => {
  const key = e.key
  const allowed = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab', 'Home', 'End', '.']
  if ((key >= '0' && key <= '9') || allowed.includes(key)) {
    if (key !== '.') return

    const currentValue = String(e.target?.value || '')
    if (currentValue.includes('.')) e.preventDefault()
    return
  }

  e.preventDefault()
}

const onGrossAmountInput = (e) => {
  const raw = (e.target.value || '').replace(/\D/g, '')
  if (raw === '') {
    form.grossAmount = 0
    e.target.value = ''
    recalculateDeductionsForGrossAmount()
    return
  }
  const num = parseInt(raw, 10) / 100
  form.grossAmount = Number(num.toFixed(2))
  e.target.value = formatCurrency(form.grossAmount)
  recalculateDeductionsForGrossAmount()
}

// const onItemAmountInput = (e, item) => {
//   const raw = (e.target.value || '').replace(/\D/g, '')
//   if (raw === '') {
//     item.amountRequired = 0
//     e.target.value = ''
//     return
//   }
//   const num = parseInt(raw, 10) / 100
//   item.amountRequired = Number(num.toFixed(2))
//   e.target.value = formatCurrency(item.amountRequired)
// }

const recalculateDeductionsForGrossAmount = () => {
  deductions.value = deductions.value.map(deduction => {
    if (deduction.inputMode === 'percentage') {
      const amount = (form.grossAmount * Number(deduction.inputValue || 0)) / 100
      return {
        ...deduction,
        percentage: Number(deduction.inputValue || 0),
        amount: Number(amount.toFixed(2))
      }
    }

    const amount = Number(deduction.inputValue || 0)
    const percentage = form.grossAmount > 0 ? (amount / form.grossAmount) * 100 : 0

    return {
      ...deduction,
      amount: Number(amount.toFixed(2)),
      percentage: Number(percentage.toFixed(2))
    }
  })
}

const openDeductionModalForCreate = () => {
  if (Number(form.grossAmount || 0) <= 0) {
    Swal.fire({
      html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>Please enter Gross Amount before adding deductions.</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      returnFocus: false,
      icon: undefined,
    }).then(() => {
      const grossAmountField = document.getElementById('grossAmount')
      if (grossAmountField) {
        grossAmountField.scrollIntoView({ behavior: 'smooth', block: 'center' })
        grossAmountField.focus()
      }
    })
    return
  }
  if (loadingDeductionOptions.value) return

  const selected = deductionOptions.value.find(d => d.id === selectedDeductionId.value)
  if (!selected) return

  const exists = deductions.value.some(d => d.id === selected.id)
  if (exists) {
    Swal.fire({
      html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>This deduction is already added</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    })
    return
  }

  deductionModalMode.value = 'create'
  editingDeductionIndex.value = null
  const selectedDedTypeMode = Number(selected.dedTypeMode ?? 0)
  const selectedDedTypeAmt = Number(selected.dedTypeAmt ?? selected.defaultValue ?? selected.percentage ?? 0)
  deductionDraft.id = selected.id
  deductionDraft.name = selected.name
  deductionDraft.description = selected.description || selected.name
  deductionDraft.dedTypeAmt = selectedDedTypeAmt
  deductionDraft.dedTypeMode = selectedDedTypeMode
  deductionDraft.dedSetByUserInd = 0
  deductionDraft.dedPercentByUser = 0
  deductionDraft.inputMode = selectedDedTypeMode === 1 ? 'percentage' : 'amount'
  deductionDraft.value = selectedDedTypeAmt
  showDeductionModal.value = true
}

const openDeductionModalForEdit = (index) => {
  const selected = deductions.value[index]
  if (!selected) return

  deductionModalMode.value = 'edit'
  editingDeductionIndex.value = index
  const selectedDedTypeMode = Number(selected.dedTypeMode ?? 0)
  const selectedDedTypeAmt = Number(selected.dedTypeAmt ?? selected.defaultValue ?? selected.percentage ?? 0)
  deductionDraft.id = selected.id
  deductionDraft.name = selected.name
  deductionDraft.description = selected.description || selected.name
  deductionDraft.dedTypeAmt = selectedDedTypeAmt
  deductionDraft.dedTypeMode = selectedDedTypeMode
  deductionDraft.dedSetByUserInd = Number(selected.dedSetByUserInd ?? 0)
  deductionDraft.dedPercentByUser = Number(selected.dedPercentByUser ?? 0)
  deductionDraft.inputMode = selectedDedTypeMode === 1 ? 'percentage' : 'amount'
  deductionDraft.value = Number(selected.inputValue ?? selectedDedTypeAmt)
  showDeductionModal.value = true
}

const closeDeductionModal = () => {
  showDeductionModal.value = false
}

const saveDeductionFromModal = () => {
  if (Number(deductionDraft.value || 0) <= 0) return

  const enteredValue = Number(deductionDraft.value || 0)
  const grossAmount = Number(form.grossAmount || 0)
  const activeInputMode = Number(deductionDraft.dedTypeMode || 0) === 1 ? 'percentage' : 'amount'

  if (activeInputMode === 'percentage' && enteredValue > 100) {
    Swal.fire({
      html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>Deduction percentage cannot be more than 100%.</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    })
    return
  }

  const attemptedAmount = activeInputMode === 'percentage'
    ? Number(((grossAmount * enteredValue) / 100).toFixed(2))
    : enteredValue

  if (attemptedAmount > grossAmount) {
    Swal.fire({
      html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>Deduction amount cannot be greater than Gross Amount.</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    })
    return
  }

  const existingDeductionAmount = deductionModalMode.value === 'edit' && editingDeductionIndex.value !== null
    ? Number(deductions.value[editingDeductionIndex.value]?.amount || 0)
    : 0
  const projectedTotalDeductions = Number(totalDeductions.value || 0) - existingDeductionAmount + attemptedAmount

  if (projectedTotalDeductions > grossAmount) {
    Swal.fire({
      html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>Total deductions cannot exceed Gross Amount.</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    })
    return
  }

  const normalizedInputMode = Number(deductionDraft.dedTypeMode || 0) === 1 ? 'percentage' : 'amount'
  const normalizedInputValue = Number(deductionDraft.value || 0)
  const computedAmount = normalizedInputMode === 'percentage'
    ? Number(((form.grossAmount * normalizedInputValue) / 100).toFixed(2))
    : Number(normalizedInputValue.toFixed(2))
  const computedPercentage = form.grossAmount > 0
    ? Number(((computedAmount / form.grossAmount) * 100).toFixed(2))
    : 0
  const defaultPercentage = Number(deductionDraft.dedTypeAmt || 0)
  const dedSetByUserInd = normalizedInputMode === 'percentage' && Math.abs(normalizedInputValue - defaultPercentage) > 0.000001 ? 1 : 0
  const dedPercentByUser = dedSetByUserInd ? normalizedInputValue : 0

  const nextDeduction = {
    id: deductionDraft.id,
    name: deductionDraft.name,
    description: deductionDraft.description || deductionDraft.name,
    dedTypeAmt: Number(deductionDraft.dedTypeAmt || 0),
    dedTypeMode: Number(deductionDraft.dedTypeMode || 0),
    dedSetByUserInd,
    dedPercentByUser,
    inputMode: normalizedInputMode,
    inputValue: normalizedInputValue,
    percentage: computedPercentage,
    amount: computedAmount
  }

  if (deductionModalMode.value === 'edit' && editingDeductionIndex.value !== null) {
    deductions.value.splice(editingDeductionIndex.value, 1, nextDeduction)
  } else {
    deductions.value.push(nextDeduction)
  }

  selectedDeductionId.value = ''
  closeDeductionModal()
}

const removeDeduction = (index) => {
  deductions.value.splice(index, 1)
}



const selectedMDA = computed(() =>
  beneficiaries.value.find(mda => String(mda.id) === String(form.beneficiary))
)

const selectedConsultant = computed(() =>
  consultantOptions.value.find(consultant => String(consultant.id) === String(form.beneficiary))
)

const selectedOriginatingMDA = computed(() =>
  beneficiaries.value.find(mda => String(mda.id) === String(form.originatingMdaId))
)

const selectedBank = computed(() =>
  banks.value.find(bank => String(bank.id) === String(form.bank))
)

watch(
  () => [form.isMDA, form.isConsultant, form.beneficiary],
  () => {
    if (form.isConsultant) {
      form.payee = selectedConsultant.value?.name || ''
      return
    }

    if (form.isMDA) {
      form.payee = selectedMDA.value?.name || ''
      return
    }

    form.payee = ''
  },
  { immediate: true }
)

const mappedDeductions = computed(() =>
  deductions.value.map(deduction => ({
    dedTypeId: deduction.id,
    dedTypeName: deduction.name,
    dedTypeAmt: Number(deduction.dedTypeAmt ?? 0),
    dedTypeMode: Number(deduction.dedTypeMode ?? 0),
    dedSetByUserInd: Number(deduction.dedSetByUserInd ?? 0),
    dedPercentByUser: Number(deduction.dedPercentByUser ?? 0),
    amount: Number(deduction.amount || 0)
  }))
)

const mappedDeductionsForUpdate = computed(() => {
  const existingDeductions = Array.isArray(existingPvPayload.value?.deductions)
    ? existingPvPayload.value.deductions
    : []

  return deductions.value.map(deduction => {
    const matchedExistingDeduction = existingDeductions.find(item =>
      String(item?.dedTypeId || item?.id || '') === String(deduction.id)
    )

    return {
      id: matchedExistingDeduction?.id || null,
      createdBy: matchedExistingDeduction?.createdBy || existingPvPayload.value?.createdBy || null,
      lastModifiedBy: existingPvPayload.value?.lastModifiedBy || matchedExistingDeduction?.lastModifiedBy || null,
      userId: matchedExistingDeduction?.userId || existingPvPayload.value?.userId || null,
      dedTypeId: deduction.id,
      dedTypeName: deduction.name,
      dedTypeAmt: Number(deduction.dedTypeAmt ?? matchedExistingDeduction?.dedTypeAmt ?? 0),
      dedTypeMode: Number(deduction.dedTypeMode ?? matchedExistingDeduction?.dedTypeMode ?? 0),
      dedSetByUserInd: Number(deduction.dedSetByUserInd ?? matchedExistingDeduction?.dedSetByUserInd ?? 0),
      dedPercentByUser: Number(deduction.dedPercentByUser ?? matchedExistingDeduction?.dedPercentByUser ?? 0),
      paymentVoucherId: matchedExistingDeduction?.paymentVoucherId || existingPvPayload.value?.id || pvIdFromQuery.value || null,
      amount: Number(deduction.amount || 0),
      newEntity: matchedExistingDeduction?.newEntity ?? false,
      version: Number(matchedExistingDeduction?.version ?? 0),
      createdDate: matchedExistingDeduction?.createdDate || null,
      lastModifiedDate: matchedExistingDeduction?.lastModifiedDate || null
    }
  })
})

const mapExistingDeductions = (source) => {
  deductions.value = (source || []).map(item => {
    const dedTypeMode = Number(item?.dedTypeMode ?? item?.mode ?? 0)
    const dedTypeAmt = Number(item?.dedTypeAmt ?? item?.defaultValue ?? 0)
    const amount = Number(item?.amount || 0)
    const percentage = Number(item?.percentage ?? (form.grossAmount > 0 ? ((amount / form.grossAmount) * 100) : 0))
    const description = item?.dedTypeName || item?.description || item?.name || 'Deduction'

    return {
      id: item?.dedTypeId || item?.id || '',
      name: item?.dedTypeName || item?.name || description,
      description,
      dedTypeAmt,
      dedTypeMode,
      dedSetByUserInd: Number(item?.dedSetByUserInd ?? 0),
      dedPercentByUser: Number(item?.dedPercentByUser ?? 0),
      inputMode: dedTypeMode === 1 ? 'percentage' : 'amount',
      inputValue: dedTypeMode === 1 ? Number(item?.inputValue ?? percentage ?? dedTypeAmt) : Number(item?.inputValue ?? amount),
      percentage,
      amount
    }
  })
}

const fetchExistingPaymentVoucher = async () => {
  if (!pvIdFromQuery.value) return

  const token = Cookies.get('auth_token')
  try {
    const { data } = await axios.get(`${root_api_url}/payment/voucher/${pvIdFromQuery.value}`, {
      headers: { Authorization: `Bearer ${token}` }
    })

    existingPvPayload.value = data
    form.paymentType = data?.paymentType ? String(data.paymentType).charAt(0).toUpperCase() + String(data.paymentType).slice(1).toLowerCase() : ''
    form.bank = data?.bankId || ''
    form.grossAmount = Number(data?.grossAmount || 0)
    form.initiatedDate = data?.paymentDate || form.initiatedDate
    form.particulars = data?.particulars || ''
    form.natureOfExpenditure = data?.natureOfExpenditure || ''
    form.payee = data?.payee || ''
    form.chequeNumber = data?.chequeNumber || ''

    const mdaId = data?.mdaId || ''
    const payee = String(data?.payee || '').trim().toLowerCase()
    const consultant = consultantOptions.value.find(c => String(c.name || '').trim().toLowerCase() === payee)

    if (consultant) {
      form.isMDA = false
      form.isConsultant = true
      form.beneficiary = consultant.id
      form.originatingMdaId = mdaId
    } else if (mdaId) {
      form.isMDA = true
      form.isConsultant = false
      form.beneficiary = mdaId
      form.originatingMdaId = ''
    }

    mapExistingDeductions(data?.deductions || [])
  } catch (error) {
    console.error('Failed to load existing payment voucher for correction:', error)
    Swal.fire({
      html: `${iconHtmlError}
      <h1 style="font-weight:600;">Error</h1>
      <p>${error?.response?.data?.errorMessage || error?.response?.data?.error || 'Failed to load returned payment voucher for correction.'}</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    })
  }
}

// const buildParticularsFromEcoItems = () => {
//   const items = economicLineItems.value || []
//   return items
//     .map(item => `${item.ecoCode || ''} - ${item.ecoLineItem || ''}: ${Number(item.amountRequired || 0)}`)
//     .join(' | ')
// }

const fetchDeductionOptions = async () => {
  loadingDeductionOptions.value = true
  const token = Cookies.get('auth_token')
  try {
    const response = await axios.get(`${root_api_url}/payment/deduction-type`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    const data = Array.isArray(response.data) ? response.data : []

    deductionOptions.value = data
      .filter(ded => Number(ded.activeInd ?? 1) !== 0)
      .map(ded => ({
        id: ded.id,
        name: ded.deductionName || ded.name || '',
        dedTypeAmt: Number(ded.dedTypeAmt ?? ded.defaultValue ?? ded.percentage ?? 0),
        dedTypeMode: Number(ded.dedTypeMode ?? ded.mode ?? 0),
        percentage: Number(ded.defaultValue ?? ded.percentage ?? 0),
        description: ded.description || ''
      }))

  } catch (error) {
    deductionOptions.value = []
    console.error('Failed to fetch deduction options:', error)
  } finally {
    loadingDeductionOptions.value = false
  }
}

// const fetchEcoOptions = async () => {
//   const token = Cookies.get('auth_token')
//   try {
//     const response = await axios.get(`${root_api_url}/eli/eco-line-item`, {
//       headers: {
//         Authorization: `Bearer ${token}`,
//         'Content-Type': 'application/json',
//       },
//     })

//     ecoOptions.value = (response.data || []).map(eco => ({
//       id: eco.id,
//       ecoCode: eco.economicCode || '',
//       ecoLineItem: eco.name || '',
//       description: eco.description || '',
//       amountBudgeted: 0
//     }))

//   } catch (error) {
//     ecoOptions.value = []
//     console.error('Failed to fetch eco line items:', error)
//   }
// }

const fetchConsultantOptions = async () => {
  loadingConsultantOptions.value = true
  const token = Cookies.get('auth_token')
  try {
    const response = await axios.get(`${root_api_url}/payment/consultant/all`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    const data = Array.isArray(response.data) ? response.data : []

    consultantOptions.value = data.map(consultant => ({
      id: consultant.id ?? consultant.name,
      name: consultant.name || ''
    }))
  } catch (error) {
    consultantOptions.value = []
    console.error('Failed to fetch consultants:', error)
  } finally {
    loadingConsultantOptions.value = false
  }
}

const fetchBanks = async () => {
  loadingBanks.value = true
  const token = Cookies.get('auth_token')
  try {
    const response = await axios.get(`${root_api_url}/payment/banks`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    const data = Array.isArray(response.data) ? response.data : []

    banks.value = data.map(bank => ({
      id: bank.id,
      name: bank.bankName || ''
    }))
  } catch (error) {
    banks.value = []
    console.error('Failed to fetch banks:', error)
  } finally {
    loadingBanks.value = false
  }
}

const fetchMDAOptions = async () => {
  loadingMDAOptions.value = true
  const token = Cookies.get('auth_token')
  try {
    const response = await axios.get(`${root_api_url}/eli/mda`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })

    const data = Array.isArray(response.data) ? response.data : []

    beneficiaries.value = data.map(mda => ({
      id: mda.id,
      name: mda.name || '',
      orgCode: mda.orgCode || ''
    }))
  } catch (error) {
    beneficiaries.value = []
    console.error('Failed to fetch MDAs:', error)
  } finally {
    loadingMDAOptions.value = false
  }
}

const handleSubmit = async () => {
  const token = Cookies.get('auth_token')

  if (isMemoMode.value && Number(form.grossAmount || 0) > Number(memoData.value?.amountApproved || 0)) {
    Swal.fire({
      html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>Gross Amount cannot exceed the Amount Budgeted (${formatCurrency(memoData.value?.amountApproved || 0)}).</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    })
    return
  }

  if (Number(totalDeductions.value || 0) > Number(form.grossAmount || 0)) {
    Swal.fire({
      html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>Total deductions cannot exceed Gross Amount.</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    })
    return
  }

  if (form.paymentType === 'Cheque' && !form.chequeNumber) {
    Swal.fire({
      html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>Kindly enter the Cheque Number.</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    })
    return
  }

  const resolvedMdaId = form.isMDA
    ? form.beneficiary
    : (form.isConsultant ? form.originatingMdaId : '')

  const resolvedMdaName = form.isMDA
    ? (selectedMDA.value?.name || '')
    : (form.isConsultant ? (selectedOriginatingMDA.value?.name || '') : '')

  const resolvedOrgCode = form.isMDA
    ? (selectedMDA.value?.orgCode || '')
    : (form.isConsultant ? (selectedOriginatingMDA.value?.orgCode || '') : '')

  // if (!economicLineItems.value || economicLineItems.value.length === 0) {
  //   Swal.fire({
  //     html: `${iconHtmlError}
  //       <h1 style="font-weight:600;">Error</h1>
  //       <p>Please add at least one economic line item</p>`,
  //     showConfirmButton: true,
  //     showCloseButton: true,
  //     icon: undefined,
  //   })
  //   return
  // }

  const paymentData = {
    payee: form.payee || (form.isConsultant ? (selectedConsultant.value?.name || '') : (selectedMDA.value?.name || '')),
    bankId: form.bank,
    bankName: selectedBank.value?.name || '',
    grossAmount: Number(form.grossAmount || 0),
    deductions: mappedDeductions.value,
    netAmount: Number(netAmount.value || 0),
    paymentDate: form.initiatedDate,
    paymentType: form.paymentType.toLowerCase(),
    natureOfExpenditure: form.natureOfExpenditure,
    particulars: form.particulars || '',
    chequeNumber: form.paymentType === 'Cheque' ? form.chequeNumber : null,
    ...(form.memoId || memoIdFromRoute.value ? { memoId: form.memoId || memoIdFromRoute.value } : {}),
    mdaId: resolvedMdaId || null,
    mdaName: resolvedMdaName,
    orgCode: resolvedOrgCode
  }

  const existing = existingPvPayload.value || {}

  const updatePayload = {
    id: existing.id || pvIdFromQuery.value,
    createdBy: existing.createdBy || null,
    lastModifiedBy: existing.lastModifiedBy || null,
    userId: existing.userId || null,
    pvNumber: existing.pvNumber || '',
    payee: paymentData.payee,
    mdaId: paymentData.mdaId || existing.mdaId || null,
    mdaName: paymentData.mdaName || existing.mdaName || '',
    orgCode: paymentData.orgCode || existing.orgCode || '',
    bankId: paymentData.bankId,
    bankName: paymentData.bankName,
    grossAmount: paymentData.grossAmount,
    deductions: mappedDeductionsForUpdate.value,
    netAmount: paymentData.netAmount,
    paymentDate: paymentData.paymentDate,
    warrantId: existing.warrantId || null,
    warrantNumber: existing.warrantNumber || '',
    memoId: paymentData.memoId || existing.memoId || null,
    pvStatus: existing.pvStatus || '',
    errorMessage: existing.errorMessage || '',
    pvInitDate: existing.pvInitDate || null,
    pvInitiatorId: existing.pvInitiatorId || null,
    ticketId: existing.ticketId || null,
    standardPVInd: Number(existing.standardPVInd ?? 1),
    paymentType: paymentData.paymentType,
    particulars: paymentData.particulars,
    chequeNumber: paymentData.chequeNumber,
    pvMonth: Number(existing.pvMonth ?? 0),
    pvYear: Number(existing.pvYear ?? 0),
    natureOfExpenditure: existing.natureOfExpenditure || '',
    standardPV: Boolean(existing.standardPV ?? true),
    newEntity: false,
    version: Number(existing.version ?? 0),
    createdDate: existing.createdDate || null,
    lastModifiedDate: existing.lastModifiedDate || null,
  }

  loading.value = true

  try {
    if (isEditMode.value && pvIdFromQuery.value) {
      await axios.put(`${root_api_url}/payment/voucher/${pvIdFromQuery.value}`, updatePayload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Returned payment voucher updated and re-submitted for approval.'
      }).then(() => {
        router.push({ name: 'ViewPaymentVouchers' })
      })
    } else {
      await axios.post(`${root_api_url}/payment/voucher/no-warrant-pv`, paymentData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Payment Voucher Generated Successfully'
      }).then(() => {
        router.push({ name: 'ViewPaymentVouchers' })
      })
    }

  } catch (error) {
    Swal.fire({
      html: `${iconHtmlError}
        <h1 style="font-weight:600;">Error</h1>
        <p>${error?.response?.data?.errorMessage || error?.response?.data?.error || 'Failed to generate payment voucher'}</p>`,
      showConfirmButton: true,
      showCloseButton: true,
      icon: undefined,
    })
  } finally {
    loading.value = false
  }
}

const formatMemoDate = (dateValue) => {
  if (!dateValue) return 'N/A'
  const date = new Date(dateValue)
  if (Number.isNaN(date.getTime())) return 'N/A'
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const openMemoPreview = () => {
  showMemoPreview.value = true
}

const closeMemoPreview = () => {
  showMemoPreview.value = false
}

onMounted(async () => {
  form.initiatedDate = new Date().toISOString().split('T')[0]

  await Promise.all([
    fetchBanks(),
    fetchMDAOptions(),
    fetchDeductionOptions(),
    fetchConsultantOptions()
  ])

  if (isEditMode.value) {
    await fetchExistingPaymentVoucher()
  }

  if (isMemoMode.value) {
    await fetchMemoDetails()
  }

  document.title = isMemoMode.value ? 'Generate Payment Voucher from PV Memo' : (isEditMode.value ? 'Edit Returned Payment Voucher' : 'Payment Voucher Generation')
})

const fetchMemoDetails = async () => {
  if (!memoIdFromRoute.value) return

  const token = Cookies.get('auth_token')
  loadingMemo.value = true

  try {
    const { data } = await axios.get(`${root_api_url}/payment/memo/${memoIdFromRoute.value}`, {
      headers: { Authorization: `Bearer ${token}` }
    })

    memoData.value = data

    // Pre-fill form fields from memo data
    if (data?.mdaId) {
      form.isMDA = true
      form.isConsultant = false
      form.beneficiary = data.mdaId
      form.originatingMdaId = ''
    }

    if (data?.amountApproved !== undefined && data?.amountApproved !== null) {
      form.grossAmount = Number(data.amountApproved || 0)
    }

    // Store the memoId for submission
    form.memoId = data?.id || memoIdFromRoute.value

  } catch (error) {
    console.error('Error fetching memo details:', error)
    await Swal.fire({
      icon: 'error',
      title: 'Error',
      text: 'Failed to load memo details. Please try again.',
    })
  } finally {
    loadingMemo.value = false
  }
}
</script>

<style scoped>
/* Top Section Border */
.top-section-border {
  border: 1px solid #d1d5db;
  border-radius: 4px;
  padding: 1.5rem;
  background-color: #ffffff;
  margin-bottom: 1.5rem;
}

/* Beneficiary Header Layout */
.beneficiary-header {
  display: flex;
  justify-content: left;
  align-items: center;
  gap: 1rem;
}

.beneficiary-header .form-label {
  white-space: nowrap;
}

/* Checkbox Group Styling */
.checkbox-group {
  display: flex;
  gap: 1.5rem;
  align-items: center;
  margin: 0;
  padding-top: 5px;
}

.checkbox-label {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  margin: 0;
  font-size: 0.9rem;
}

.checkbox-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
  margin: 0;
}

.checkbox-label span {
  user-select: none;
  white-space: nowrap !important;
}

/* Table Styling */
.table-header th {
  background-color: #f3f4f6;
  font-weight: 600;
  color: #374151;
  border: 1px solid #e5e7eb;
  padding: 0.75rem;
  font-size: 0.9rem;
}

.table tbody td {
  vertical-align: middle;
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
}

.deduction-actions {
  display: flex;
  gap: 0.4rem;
  justify-content: center;
}

/* Mobile Card Layout for Tables */
@media (max-width: 768px) {
  .table-responsive {
    border: none;
  }

  .table thead {
    display: none;
  }

  .table tbody {
    display: block;
  }

  .table tbody tr {
    display: block;
    margin-bottom: 1rem;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    background-color: #ffffff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .table tbody td {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 1rem;
    border: none;
    border-bottom: 1px solid #f3f4f6;
    text-align: left !important;
  }

  .table tbody td:last-child {
    border-bottom: none;
    justify-content: center;
  }

  .table tbody td::before {
    content: attr(data-label);
    font-weight: 600;
    color: #374151;
    margin-right: 1rem;
    flex-shrink: 0;
  }

  .table tbody td:last-child::before {
    content: '';
  }

  .table tbody td input {
    max-width: 150px;
    margin-left: auto;
  }
}

/* Add Section Styling */
.add-section {
  margin-top: 1.5rem;
  padding: 1rem;
  background-color: #f9fafb;
  border-radius: 4px;
}

.section-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.75rem;
}

.add-controls-wrapper {
  display: flex;
  justify-content: center;
}

.add-controls {
  display: flex;
  align-items: flex-end;
  gap: 1rem;
  flex-wrap: nowrap;
}

.filter-group,
.select-group,
.button-group {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.filter-group {
  flex: 0 0 160px;
}

.select-group {
  flex: 0 0 220px;
}

.button-group {
  flex: 0 0 auto;
}

.button-group .control-label {
  visibility: hidden;
  height: 1.4rem;
}

.control-label {
  font-size: 0.8rem;
  color: #6b7280;
  margin-bottom: 0.25rem;
}

/* Submit Button */
.btn-submit {
  background-color: #10b981;
  border-color: #10b981;
  color: #ffffff;
  padding: 0.625rem 3rem;
  font-weight: 500;
  border-radius: 4px;
  font-size: 1rem;
}

.btn-submit:hover {
  background-color: #059669;
  border-color: #059669;
  color: #ffffff;
}

.btn-submit:disabled {
  background-color: #9ca3af;
  border-color: #9ca3af;
  cursor: not-allowed;
}

/* Form Control Enhancements */
.form-control:focus,
.form-select:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 0.2rem rgba(59, 130, 246, 0.25);
}

.form-control[readonly] {
  background-color: #f3f4f6;
  cursor: not-allowed;
}

/* Responsive Adjustments */
@media (max-width: 768px) {
  .beneficiary-header {
    flex-wrap: wrap;
  }

  .checkbox-group {
    gap: 1rem;
  }

  .add-controls-wrapper {
    justify-content: stretch;
  }

  .add-controls {
    flex-direction: column;
    align-items: stretch;
    width: 100%;
  }

  .filter-group,
  .select-group,
  .button-group {
    flex: 1 1 100%;
    width: 100%;
  }

  .button-group {
    align-items: stretch;
    margin-top: 0.5rem;
  }

  .button-group .control-label {
    display: none;
  }

  .button-group .btn {
    width: 100%;
  }

}

.payment-type-select {
  margin-top: 5px !important;
}

.memo-preview-btn {
  background-color: #3b82f6;
  color: #ffffff;
  border: none;
}

.memo-preview-btn:hover {
  background-color: #2563eb;
  color: #ffffff;
}

.warrant-preview-overlay {
  position: fixed;
  inset: 0;
  z-index: 1060;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.warrant-preview-modal {
  width: min(920px, 100%);
  max-height: 90vh;
  overflow: hidden;
  border-radius: 8px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
}

.warrant-preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.warrant-preview-body {
  padding: 1rem;
  overflow: auto;
}

.preview-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.6rem;
}

.preview-item {
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 0.6rem 0.75rem;
}

.preview-item small {
  color: #6b7280;
  font-size: 0.75rem;
}

.preview-item p {
  color: var(--color);
  font-weight: 600;
}

.deduction-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1060;
  padding: 1rem;
}

.deduction-modal {
  background: #ffffff;
  width: min(560px, 100%);
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.deduction-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e5e7eb;
  padding: 0.85rem 1rem;
}

.deduction-modal-body {
  padding: 1rem;
}

.deduction-modal-footer {
  border-top: 1px solid #e5e7eb;
  padding: 0.85rem 1rem;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.deduction-selected {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 0.6rem 0.75rem;
}

.deduction-selected small {
  color: #6b7280;
  font-size: 0.75rem;
}

.deduction-selected p {
  color: #111827;
  font-weight: 600;
}

.deduction-input-mode {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.deduction-input-mode .mode-disabled {
  color: #9ca3af;
  opacity: 0.7;
}

.deduction-input-mode .mode-disabled span {
  color: #9ca3af;
}

.deduction-preview-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.6rem;
}

.deduction-preview-item {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 0.6rem 0.75rem;
}

.deduction-preview-item small {
  color: #6b7280;
  font-size: 0.75rem;
}

.deduction-preview-item p {
  color: #111827;
  font-weight: 600;
}

.deduction-hint {
  color: #6b7280;
  font-size: 0.82rem;
}

@media (max-width: 576px) {
  .deduction-preview-grid {
    grid-template-columns: 1fr;
  }
}
</style>
